package br.com.htms.inspeccaocampo.platform

import br.com.htms.inspeccaocampo.util.Base64Codec
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException
import kotlin.js.Date
import kotlin.js.unsafeCast
import kotlinx.browser.document
import kotlinx.browser.localStorage
import kotlinx.coroutines.suspendCancellableCoroutine
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Uint8Array
import org.khronos.webgl.get
import org.khronos.webgl.set
import org.w3c.dom.HTMLAnchorElement
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag
import org.w3c.files.FileReader

/**
 * Persistência web:
 * - memória (leitura síncrona após warmUp / escrita)
 * - IndexedDB (cache durável de fotos/rascunhos — supera quota do localStorage)
 * - aliases (path local ↔ URL R2) para o PDF achar bytes mesmo se o path do form mudar
 * - leitura on-demand do IDB se a memória perder a chave (cache miss após warmUp)
 * - download via shareFile/downloadBytes só para exportações (PDF/ZIP/config)
 */
actual object PlatformFiles {
    private const val LS_PREFIX = "icd_fs:"
    private const val DB_NAME = "icd_fs_v1"
    private const val STORE = "files"
    private const val ALIAS_STORE = "aliases"
    private val memory = mutableMapOf<String, ByteArray>()
    /** remoteOrLocal → otherKey */
    private val aliases = mutableMapOf<String, String>()
    private var ready = false

    actual fun documentsDir(): String = "/documents"
    actual fun cacheDir(): String = "/cache"

    actual fun ensureDir(path: String) {
        // Virtual FS — no-op
    }

    actual fun writeBytes(path: String, bytes: ByteArray) {
        memory[path] = bytes
        runCatching { localStorage.removeItem(LS_PREFIX + path) }
        persistToIdb(path, bytes)
    }

    /**
     * Grava em memória e espera o IndexedDB confirmar (crítico para fotos/histórico no mobile).
     */
    actual suspend fun writeBytesDurable(path: String, bytes: ByteArray): Boolean {
        memory[path] = bytes
        runCatching { localStorage.removeItem(LS_PREFIX + path) }
        return persistToIdbAwait(path, bytes)
    }

    actual suspend fun writeTextDurable(path: String, text: String): Boolean =
        writeBytesDurable(path, text.encodeToByteArray())

    /** Liga path local e URL remota para fallback no PDF. */
    fun linkAlias(a: String, b: String) {
        if (a.isBlank() || b.isBlank() || a == b) return
        aliases[a] = b
        aliases[b] = a
        persistAlias(a, b)
    }

    fun aliasOf(path: String): String? = aliases[path]

    actual fun writeText(path: String, text: String) {
        writeBytes(path, text.encodeToByteArray())
    }

    actual fun readBytes(path: String): ByteArray? {
        memory[path]?.takeIf { it.isNotEmpty() }?.let { return it }
        aliases[path]?.let { alt ->
            memory[alt]?.takeIf { it.isNotEmpty() }?.let { return it }
        }
        val b64 = localStorage.getItem(LS_PREFIX + path) ?: return null
        return Base64Codec.decode(b64)?.also {
            memory[path] = it
            persistToIdb(path, it)
            runCatching { localStorage.removeItem(LS_PREFIX + path) }
        }
    }

    /**
     * Leitura assíncrona: memória → alias → localStorage → IndexedDB (get pontual).
     * Usar no PDF/export quando readBytes síncrono falhar.
     */
    suspend fun readBytesAsync(path: String): ByteArray? {
        readBytes(path)?.takeIf { it.isNotEmpty() }?.let { return it }
        val fromIdb = readFromIdb(path)
        if (fromIdb != null && fromIdb.isNotEmpty()) {
            memory[path] = fromIdb
            consoleWarn("readBytesAsync: recuperou do IDB ($path, ${fromIdb.size} bytes)")
            return fromIdb
        }
        val alt = aliases[path]
        if (!alt.isNullOrBlank()) {
            readBytes(alt)?.takeIf { it.isNotEmpty() }?.let { return it }
            val altIdb = readFromIdb(alt)
            if (altIdb != null && altIdb.isNotEmpty()) {
                memory[alt] = altIdb
                memory[path] = altIdb
                consoleWarn("readBytesAsync: recuperou alias do IDB ($alt → $path)")
                return altIdb
            }
        }
        // Alias pode estar só no IDB (warmUp falhou / memória limpa)
        val aliasFromIdb = readAliasFromIdb(path)
        if (!aliasFromIdb.isNullOrBlank()) {
            aliases[path] = aliasFromIdb
            aliases[aliasFromIdb] = path
            readBytes(aliasFromIdb)?.takeIf { it.isNotEmpty() }?.let { return it }
            val otherBytes = readFromIdb(aliasFromIdb)
            if (otherBytes != null && otherBytes.isNotEmpty()) {
                memory[aliasFromIdb] = otherBytes
                memory[path] = otherBytes
                consoleWarn("readBytesAsync: alias IDB + bytes ($path ↔ $aliasFromIdb)")
                return otherBytes
            }
        }
        return null
    }

    actual fun readText(path: String): String? = readBytes(path)?.decodeToString()

    actual fun exists(path: String): Boolean =
        memory.containsKey(path) ||
            aliases[path]?.let { memory.containsKey(it) } == true ||
            localStorage.getItem(LS_PREFIX + path) != null

    actual fun delete(path: String): Boolean {
        memory.remove(path)
        runCatching { localStorage.removeItem(LS_PREFIX + path) }
        deleteFromIdb(path)
        return true
    }

    actual fun listFiles(dir: String): List<String> {
        val prefix = if (dir.endsWith("/")) dir else "$dir/"
        val keys = mutableSetOf<String>()
        keys.addAll(
            memory.keys.filter {
                it.startsWith(prefix) || it.substringBeforeLast('/') == dir.trimEnd('/')
            }
        )
        for (i in 0 until localStorage.length) {
            val k = localStorage.key(i) ?: continue
            if (!k.startsWith(LS_PREFIX)) continue
            val path = k.removePrefix(LS_PREFIX)
            if (path.startsWith(prefix) || path.substringBeforeLast('/') == dir.trimEnd('/')) {
                keys += path
            }
        }
        return keys.sorted()
    }

    actual fun join(parent: String, child: String): String {
        if (parent.endsWith("/")) return parent + child
        return "$parent/$child"
    }

    actual fun shareFile(path: String, mimeType: String, title: String) {
        val bytes = readBytes(path) ?: return
        downloadBytes(bytes, path.substringAfterLast('/'), mimeType)
    }

    fun downloadBytes(bytes: ByteArray, fileName: String, mimeType: String) {
        runCatching {
            val uint8 = Uint8Array(bytes.size)
            bytes.forEachIndexed { i, b -> uint8[i] = b }
            val blob = Blob(arrayOf(uint8), BlobPropertyBag(type = mimeType))
            val url = js("URL").createObjectURL(blob) as String
            val a = document.createElement("a") as HTMLAnchorElement
            a.href = url
            a.download = fileName
            document.body?.appendChild(a)
            a.click()
            document.body?.removeChild(a)
            js("URL").revokeObjectURL(url)
        }
    }

    actual suspend fun warmUp() {
        if (ready) return
        migrateLocalStorageToMemory()
        val result = runCatching { loadAllFromIdb() }
        result.onFailure {
            consoleWarn("IndexedDB warmUp FALHOU (histórico/fotos podem sumir): ${it.message}")
            // Não marca ready=true se IDB falhou — tentativas on-demand ainda podem funcionar
            // se o DB abrir depois. Ainda assim liberamos a UI.
        }
        result.onSuccess {
            consoleWarn("IndexedDB warmUp ok: ${memory.size} arquivos, ${aliases.size} aliases")
        }
        memory.forEach { (path, bytes) -> persistToIdb(path, bytes) }
        ready = true
    }

    private fun migrateLocalStorageToMemory() {
        val toRemove = mutableListOf<String>()
        for (i in 0 until localStorage.length) {
            val k = localStorage.key(i) ?: continue
            if (!k.startsWith(LS_PREFIX)) continue
            val path = k.removePrefix(LS_PREFIX)
            if (memory.containsKey(path)) {
                toRemove += k
                continue
            }
            val b64 = localStorage.getItem(k) ?: continue
            val bytes = Base64Codec.decode(b64) ?: continue
            memory[path] = bytes
            toRemove += k
        }
        toRemove.forEach { runCatching { localStorage.removeItem(it) } }
    }

    private fun ensureStores(db: dynamic) {
        try {
            val names = db.objectStoreNames
            if (!(names.contains(STORE) as Boolean)) db.createObjectStore(STORE)
            if (!(names.contains(ALIAS_STORE) as Boolean)) db.createObjectStore(ALIAS_STORE)
        } catch (_: Throwable) {
            runCatching { db.createObjectStore(STORE) }
            runCatching { db.createObjectStore(ALIAS_STORE) }
        }
    }

    private suspend fun loadAllFromIdb() {
        val pendingBlobs = mutableListOf<Pair<String, dynamic>>()
        suspendCancellableCoroutine { cont ->
            val indexedDB = js("window.indexedDB")
            if (indexedDB == null || indexedDB == undefined) {
                cont.resume(Unit)
                return@suspendCancellableCoroutine
            }
            val openReq = indexedDB.open(DB_NAME, 2)
            openReq.onupgradeneeded = {
                ensureStores(openReq.result)
            }
            openReq.onerror = {
                cont.resumeWithException(RuntimeException("Falha ao abrir IndexedDB"))
            }
            openReq.onsuccess = {
                try {
                    val db = openReq.result
                    val tx = db.transaction(arrayOf(STORE, ALIAS_STORE), "readonly")
                    val store = tx.objectStore(STORE)
                    val req = store.openCursor()
                    var loaded = 0
                    var skipped = 0
                    req.onsuccess = {
                        val cursor = req.result
                        if (cursor != null && cursor != undefined) {
                            val key = cursor.key as? String
                            val value = cursor.value
                            if (key != null && value != null && value != undefined) {
                                val bytes = valueToBytes(value)
                                if (bytes != null && bytes.isNotEmpty()) {
                                    memory[key] = bytes
                                    loaded++
                                } else {
                                    val ctor = try {
                                        (value.asDynamic().constructor?.name as? String).orEmpty()
                                    } catch (_: Throwable) {
                                        ""
                                    }
                                    if (ctor == "Blob" || ctor == "File") {
                                        pendingBlobs.add(key to value)
                                    }
                                    skipped++
                                }
                            }
                            cursor.asDynamic().`continue`()
                        }
                    }
                    runCatching {
                        val aliasStore = tx.objectStore(ALIAS_STORE)
                        val aliasReq = aliasStore.openCursor()
                        aliasReq.onsuccess = {
                            val cursor = aliasReq.result
                            if (cursor != null && cursor != undefined) {
                                val key = cursor.key as? String
                                val other = cursor.value as? String
                                if (key != null && !other.isNullOrBlank()) {
                                    aliases[key] = other
                                    aliases[other] = key
                                }
                                cursor.asDynamic().`continue`()
                            }
                        }
                    }
                    tx.oncomplete = {
                        if (skipped > 0) {
                            consoleWarn(
                                "IndexedDB warmUp: $loaded ok, $skipped valores ilegíveis " +
                                    "(${pendingBlobs.size} Blob pendentes)"
                            )
                        }
                        db.close()
                        if (cont.isActive) cont.resume(Unit)
                    }
                    tx.onerror = {
                        db.close()
                        if (cont.isActive) cont.resumeWithException(RuntimeException("Falha ao ler IndexedDB"))
                    }
                } catch (t: Throwable) {
                    if (cont.isActive) cont.resumeWithException(t)
                }
            }
        }
        // Após fechar o cursor: converte Blobs (Safari) para ByteArray
        for ((key, blob) in pendingBlobs) {
            val bytes = blobToBytes(blob)
            if (bytes != null && bytes.isNotEmpty()) {
                memory[key] = bytes
                // Regrava como ArrayBuffer para próximas leituras síncronas
                persistToIdb(key, bytes)
                consoleWarn("IndexedDB warmUp: Blob convertido ($key, ${bytes.size}b)")
            }
        }
    }

    private suspend fun readFromIdb(path: String): ByteArray? {
        val raw: dynamic = suspendCancellableCoroutine { cont ->
            val indexedDB = js("window.indexedDB")
            if (indexedDB == null || indexedDB == undefined) {
                cont.resume(null)
                return@suspendCancellableCoroutine
            }
            try {
                val openReq = indexedDB.open(DB_NAME, 2)
                openReq.onupgradeneeded = { ensureStores(openReq.result) }
                openReq.onerror = {
                    consoleWarn("IDB get open falhou: $path")
                    if (cont.isActive) cont.resume(null)
                }
                openReq.onsuccess = {
                    try {
                        val db = openReq.result
                        val tx = db.transaction(STORE, "readonly")
                        val req = tx.objectStore(STORE).get(path)
                        req.onsuccess = {
                            val result = req.result
                            db.close()
                            if (cont.isActive) cont.resume(result)
                        }
                        req.onerror = {
                            consoleWarn("IDB get falhou: $path")
                            db.close()
                            if (cont.isActive) cont.resume(null)
                        }
                    } catch (t: Throwable) {
                        consoleWarn("IDB get: ${t.message}")
                        if (cont.isActive) cont.resume(null)
                    }
                }
            } catch (t: Throwable) {
                consoleWarn("IDB get: ${t.message}")
                if (cont.isActive) cont.resume(null)
            }
        }
        if (raw == null || raw == undefined) return null
        valueToBytes(raw)?.takeIf { it.isNotEmpty() }?.let { return it }
        // Safari às vezes devolve Blob — lê via FileReader
        val ctor = try {
            (raw.asDynamic().constructor?.name as? String).orEmpty()
        } catch (_: Throwable) {
            ""
        }
        if (ctor == "Blob" || ctor == "File") {
            return blobToBytes(raw)
        }
        return null
    }

    private suspend fun readAliasFromIdb(path: String): String? =
        suspendCancellableCoroutine { cont ->
            val indexedDB = js("window.indexedDB")
            if (indexedDB == null || indexedDB == undefined) {
                cont.resume(null)
                return@suspendCancellableCoroutine
            }
            try {
                val openReq = indexedDB.open(DB_NAME, 2)
                openReq.onupgradeneeded = { ensureStores(openReq.result) }
                openReq.onerror = { if (cont.isActive) cont.resume(null) }
                openReq.onsuccess = {
                    try {
                        val db = openReq.result
                        val tx = db.transaction(ALIAS_STORE, "readonly")
                        val req = tx.objectStore(ALIAS_STORE).get(path)
                        req.onsuccess = {
                            val other = req.result as? String
                            db.close()
                            if (cont.isActive) cont.resume(other?.takeIf { it.isNotBlank() })
                        }
                        req.onerror = {
                            db.close()
                            if (cont.isActive) cont.resume(null)
                        }
                    } catch (_: Throwable) {
                        if (cont.isActive) cont.resume(null)
                    }
                }
            } catch (_: Throwable) {
                if (cont.isActive) cont.resume(null)
            }
        }

    private fun persistToIdb(path: String, bytes: ByteArray) {
        val indexedDB = js("window.indexedDB")
        if (indexedDB == null || indexedDB == undefined) return
        try {
            val openReq = indexedDB.open(DB_NAME, 2)
            openReq.onupgradeneeded = { ensureStores(openReq.result) }
            openReq.onsuccess = {
                try {
                    val db = openReq.result
                    val tx = db.transaction(STORE, "readwrite")
                    val store = tx.objectStore(STORE)
                    // ArrayBuffer é mais confiável no Safari iOS que Uint8Array puro
                    store.put(bytesToArrayBuffer(bytes), path)
                    tx.oncomplete = { db.close() }
                    tx.onerror = {
                        consoleWarn("IndexedDB put falhou: $path")
                        db.close()
                    }
                } catch (t: Throwable) {
                    consoleWarn("IndexedDB put: ${t.message}")
                }
            }
            openReq.onerror = {
                consoleWarn("IndexedDB open falhou ao gravar $path")
            }
        } catch (t: Throwable) {
            consoleWarn("IndexedDB: ${t.message}")
        }
    }

    private suspend fun persistToIdbAwait(path: String, bytes: ByteArray): Boolean =
        suspendCancellableCoroutine { cont ->
            val indexedDB = js("window.indexedDB")
            if (indexedDB == null || indexedDB == undefined) {
                cont.resume(true) // só memória — melhor que falhar a foto
                return@suspendCancellableCoroutine
            }
            try {
                val openReq = indexedDB.open(DB_NAME, 2)
                openReq.onupgradeneeded = { ensureStores(openReq.result) }
                openReq.onerror = {
                    consoleWarn("IndexedDB open await falhou: $path")
                    if (cont.isActive) cont.resume(false)
                }
                openReq.onsuccess = {
                    try {
                        val db = openReq.result
                        val tx = db.transaction(STORE, "readwrite")
                        tx.objectStore(STORE).put(bytesToArrayBuffer(bytes), path)
                        tx.oncomplete = {
                            db.close()
                            if (cont.isActive) cont.resume(true)
                        }
                        tx.onerror = {
                            consoleWarn("IndexedDB await put falhou: $path")
                            db.close()
                            if (cont.isActive) cont.resume(false)
                        }
                    } catch (t: Throwable) {
                        consoleWarn("IndexedDB await: ${t.message}")
                        if (cont.isActive) cont.resume(false)
                    }
                }
            } catch (t: Throwable) {
                consoleWarn("IndexedDB await: ${t.message}")
                if (cont.isActive) cont.resume(false)
            }
        }

    private fun persistAlias(a: String, b: String) {
        val indexedDB = js("window.indexedDB")
        if (indexedDB == null || indexedDB == undefined) return
        try {
            val openReq = indexedDB.open(DB_NAME, 2)
            openReq.onupgradeneeded = { ensureStores(openReq.result) }
            openReq.onsuccess = {
                try {
                    val db = openReq.result
                    val tx = db.transaction(ALIAS_STORE, "readwrite")
                    val store = tx.objectStore(ALIAS_STORE)
                    store.put(b, a)
                    store.put(a, b)
                    tx.oncomplete = { db.close() }
                    tx.onerror = {
                        consoleWarn("IndexedDB alias put falhou: $a ↔ $b")
                        db.close()
                    }
                } catch (t: Throwable) {
                    consoleWarn("IndexedDB alias: ${t.message}")
                }
            }
            openReq.onerror = {
                consoleWarn("IndexedDB alias open falhou")
            }
        } catch (t: Throwable) {
            consoleWarn("IndexedDB alias: ${t.message}")
        }
    }

    private fun deleteFromIdb(path: String) {
        val indexedDB = js("window.indexedDB")
        if (indexedDB == null || indexedDB == undefined) return
        try {
            val openReq = indexedDB.open(DB_NAME, 2)
            openReq.onsuccess = {
                try {
                    val db = openReq.result
                    val tx = db.transaction(STORE, "readwrite")
                    tx.objectStore(STORE).delete(path)
                    tx.oncomplete = { db.close() }
                    tx.onerror = {
                        consoleWarn("IndexedDB delete falhou: $path")
                        db.close()
                    }
                } catch (t: Throwable) {
                    consoleWarn("IndexedDB delete: ${t.message}")
                }
            }
        } catch (t: Throwable) {
            consoleWarn("IndexedDB delete: ${t.message}")
        }
    }

    private fun bytesToArrayBuffer(bytes: ByteArray): ArrayBuffer {
        val u = Uint8Array(bytes.size)
        bytes.forEachIndexed { i, b -> u[i] = b }
        // Copia o slice exato (evita buffer maior que byteLength em alguns engines)
        return u.buffer.slice(u.byteOffset, u.byteOffset + u.byteLength)
    }

    /**
     * Safari/iOS devolve tipos nativos do IDB (ArrayBuffer, Blob, TypedArray).
     *
     * IMPORTANTE: NÃO usar js("value instanceof X") — em Kotlin/JS o parâmetro local
     * NÃO é visível dentro de js("...") (vira global undefined) e TODOS os checks
     * falhavam → warmUp nunca restaurava fotos → PDF sem imagem.
     */
    private fun valueToBytes(value: dynamic): ByteArray? {
        return try {
            if (value == null || value == undefined) return null

            // Kotlin/JS ByteArray é Int8Array por baixo
            val asKotlinBytes = value as? ByteArray
            if (asKotlinBytes != null) {
                return asKotlinBytes.takeIf { it.isNotEmpty() }
            }

            val ctorName = try {
                (value.asDynamic().constructor?.name as? String).orEmpty()
            } catch (_: Throwable) {
                ""
            }

            when (ctorName) {
                "Uint8Array", "Int8Array", "Uint8ClampedArray" -> {
                    val view = value.unsafeCast<Uint8Array>()
                    if (view.length <= 0) return null
                    return ByteArray(view.length) { i -> view[i] }
                }
                "ArrayBuffer" -> {
                    val buf = value.unsafeCast<ArrayBuffer>()
                    val u8 = Uint8Array(buf)
                    if (u8.length <= 0) return null
                    return ByteArray(u8.length) { i -> u8[i] }
                }
                "Blob", "File" -> {
                    // Leitura síncrona de Blob é impossível aqui — caller deve usar async.
                    // Tentamos via FileReaderSync se disponível (workers); senão null + log.
                    consoleWarn("valueToBytes: Blob no IDB (use readFromIdb async); size=${value.size}")
                    return null
                }
            }

            // TypedArray genérico (DataView / outros)
            try {
                val byteLength = value.byteLength as? Int
                val buffer = value.buffer
                if (byteLength != null && byteLength > 0 && buffer != null && buffer != undefined) {
                    val offset = (value.byteOffset as? Int) ?: 0
                    val u8 = Uint8Array(buffer as ArrayBuffer, offset, byteLength)
                    return ByteArray(u8.length) { i -> u8[i] }
                }
            } catch (_: Throwable) {
            }

            // Último recurso: copiar como Int8Array view se length existir
            try {
                val len = value.length as? Int
                if (len != null && len > 0) {
                    val u8 = Uint8Array(len)
                    for (i in 0 until len) {
                        u8[i] = ((value[i] as Number).toInt() and 0xff).toByte()
                    }
                    return ByteArray(len) { i -> u8[i] }
                }
            } catch (_: Throwable) {
            }

            consoleWarn("valueToBytes: tipo não suportado ($ctorName)")
            null
        } catch (t: Throwable) {
            consoleWarn("valueToBytes: ${t.message}")
            null
        }
    }

    /**
     * Converte Blob do IDB (Safari às vezes devolve Blob) para ByteArray.
     */
    suspend fun blobToBytes(blob: dynamic): ByteArray? =
        suspendCancellableCoroutine { cont ->
            try {
                val reader = FileReader()
                reader.onload = {
                    try {
                        val buf = reader.result as? ArrayBuffer
                        if (buf == null) {
                            if (cont.isActive) cont.resume(null)
                        } else {
                            val u8 = Uint8Array(buf)
                            if (cont.isActive) cont.resume(ByteArray(u8.length) { i -> u8[i] })
                        }
                    } catch (_: Throwable) {
                        if (cont.isActive) cont.resume(null)
                    }
                }
                reader.onerror = { if (cont.isActive) cont.resume(null) }
                reader.readAsArrayBuffer(blob.unsafeCast<Blob>())
            } catch (_: Throwable) {
                if (cont.isActive) cont.resume(null)
            }
        }

    private fun consoleWarn(msg: String) {
        try {
            js("console").warn("[ICD FS] $msg")
        } catch (_: Throwable) {
        }
    }
}

actual fun yearOf(epochMs: Long): Int = Date(epochMs.toDouble()).getFullYear()

actual fun monthOf(epochMs: Long): Int = Date(epochMs.toDouble()).getMonth()

actual fun formatMonthYearPt(ano: Int, mes0: Int): String {
    val nomes = listOf(
        "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
        "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
    )
    val nome = nomes.getOrElse(mes0) { "Mês" }
    return "$nome/$ano"
}

actual fun formatDateTimeBr(epochMs: Long): String = formatDateBr(epochMs)
